import type { ReactNode } from 'react';

/**
 * Reusable vendor-console metric card. Matches the dark slate chrome. The value
 * is `min-w-0` + truncated so a long value can never blow out the grid and
 * cause horizontal page scroll.
 */
export function StatCard({
  label,
  value,
  tone = 'default',
  sub,
  accessory,
}: {
  label: string;
  value: string | number;
  tone?: 'default' | 'good' | 'bad' | 'warn';
  /** Small secondary line under the value (e.g. a rate %). */
  sub?: ReactNode;
  /** Optional right-aligned visual (e.g. a MiniDonut). */
  accessory?: ReactNode;
}): JSX.Element {
  const color =
    tone === 'bad'
      ? 'text-red-400'
      : tone === 'good'
        ? 'text-emerald-400'
        : tone === 'warn'
          ? 'text-amber-400'
          : 'text-white';
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-slate-800 bg-slate-900 p-4">
      <div className="min-w-0">
        <div className="truncate text-xs uppercase tracking-wide text-slate-500" title={label}>
          {label}
        </div>
        <div className={`mt-1 truncate text-2xl font-semibold ${color}`} title={String(value)}>
          {value}
        </div>
        {sub != null && (
          <div className="mt-0.5 truncate text-xs text-slate-400">{sub}</div>
        )}
      </div>
      {accessory && <div className="shrink-0">{accessory}</div>}
    </div>
  );
}
